import React, { useMemo, useRef } from "react";
import { Card } from "antd";
import { FileTextOutlined, GlobalOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useLanguage } from "src/hooks/UseLanguage";
import { formatNumber, getNumberLocale } from "src/utils/numberFormat";
import "./Styles/Home.scss";
import { useSocialListeningHistory } from "./Hooks/UseSocialListeningHistory";
import { formatReportDate, getRunTitle } from "./Utils/HistoryUtils";
import { SOCIAL_LISTENING_ROUTES } from "./Utils/SocialListeningConstants";

const HIGHLIGHT_CARDS = [
  { key: "Ai", icon: <img src="/Icons/Deployment-unit.svg" className="card-icon" alt="" /> },
  { key: "Region", icon: <GlobalOutlined /> },
  { key: "Actionable", icon: <img src="/Icons/Statistics.svg" className="card-icon" alt="" /> },
];

const ANALYSIS_CARDS = [
  {
    titleKey: "analyzeBrandTitle",
    lineKeys: ["analyzeBrandLine1", "analyzeBrandLine2"],
    buttonKey: "startBrandAnalysis",
    icon: "/Icons/graph.svg",
    route: SOCIAL_LISTENING_ROUTES.brandAnalysis,
  },
  {
    titleKey: "competitiveAnalysisTitle",
    lineKeys: ["competitiveAnalysisLine1", "competitiveAnalysisLine2"],
    buttonKey: "startCompetitiveAnalysis",
    icon: "/Icons/Globe-card.svg",
    route: SOCIAL_LISTENING_ROUTES.competitiveAnalysis,
  },
];

const CompetitionOverview: React.FC = () => {
  const navigate = useNavigate();
  const analysisSectionRef = useRef<HTMLDivElement | null>(null);
  const { runs } = useSocialListeningHistory();
  const { t, i18n } = useTranslation();
  useLanguage();
  const numberLocale = getNumberLocale(i18n.language);

  const localizeNumbersInText = (value: string) =>
    value.replace(/\d[\d,]*/g, match => {
      const normalizedValue = Number(match.replace(/,/g, ""));
      if (Number.isNaN(normalizedValue)) {return match;}
      return formatNumber(normalizedValue, {}, i18n.language);
    });

  const recentHistory = useMemo(() => runs.slice(0, 2), [runs]);

  const handleScrollToAnalysis = () => {
    analysisSectionRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div className="competition-container">
      {/* Header Section */}
       <div className="social-hero-graphic">
          <img
            src="/Icons/social-listening.svg"
            alt={t("socialListingAgent.heroAlt")}
          />
        </div>
      <div className="competition-header">
       
        <h1 className="competition-title">
          {t("socialListingAgent.heroTitleLine1")}
          <br />
          {t("socialListingAgent.heroTitleLine2")}
        </h1>
        <p className="competition-subtitle">
          {t("socialListingAgent.heroSubtitleLine1")}
          <br />
          {t("socialListingAgent.heroSubtitleLine2")}
        </p>
        <button
          className="button button--primary hero-cta"
          type="button"
          onClick={handleScrollToAnalysis}
        >
          {t("socialListingAgent.tryNow")}
        </button>
      </div>

      {/* Card Section */}
      <div className="competition-card-grid">
        {HIGHLIGHT_CARDS.map(({ key, icon }) => (
          <Card key={key} className="competition-card" bordered={false}>
            <div className="card-icon-circle">{icon}</div>
            <h3 className="social-card-title">{t(`socialListingAgent.card${key}Title`)}</h3>
            <p className="card-description">{t(`socialListingAgent.card${key}Description`)}</p>
          </Card>
        ))}
      </div>

      {/* New "Before I begin" Section */}
      <div className="data-scope-section">
        <Card className="data-scope-card" bordered={false}>
          <h2>{t("socialListingAgent.scopeTitle")}</h2>
          <ul className="scope-grid">
            <li>
              <span className="scope-label">
                {t("socialListingAgent.scopeDataSourceLabel")}
              </span>
              <span className="scope-text">
                {t("socialListingAgent.scopeDataSourcePrefix")}{" "}
                <span className="scope-emphasis">
                  {localizeNumbersInText(
                    t("socialListingAgent.scopeDataSourceEmphasis")
                  )}
                </span>
                .
              </span>
            </li>
            <li>
              <span className="scope-label">
                {t("socialListingAgent.scopeVerificationLabel")}
              </span>
              <span className="scope-text">
                {t("socialListingAgent.scopeVerificationPrefix")}{" "}
                <span className="scope-emphasis">
                  {t("socialListingAgent.scopeVerificationEmphasis")}
                </span>
                , {t("socialListingAgent.scopeVerificationMiddle")}{" "}
                <span className="scope-emphasis">
                  {localizeNumbersInText(
                    t("socialListingAgent.scopeVerificationTopFive")
                  )}
                </span>
                . {t("socialListingAgent.scopeVerificationSuffix")}
              </span>
            </li>
            <li>
              <span className="scope-label">
                {t("socialListingAgent.scopeCommentsLabel")}
              </span>
              <span className="scope-text">
                {t("socialListingAgent.scopeCommentsPrefix")}{" "}
                <span className="scope-emphasis">
                  {localizeNumbersInText(
                    t("socialListingAgent.scopeCommentsEmphasis")
                  )}
                </span>{" "}
                {t("socialListingAgent.scopeCommentsSuffix")}
              </span>
            </li>
            <li>
              <span className="scope-label">
                {t("socialListingAgent.scopeMonthlyLimitLabel")}
              </span>
              <span className="scope-text">
                {t("socialListingAgent.scopeMonthlyLimitPrefix")}{" "}
                <span className="scope-emphasis">
                  {localizeNumbersInText(
                    t("socialListingAgent.scopeMonthlyLimitEmphasis")
                  )}
                </span>
                .
              </span>
            </li>
            <li>
              <span className="scope-label">
                {t("socialListingAgent.scopeSessionLimitLabel")}
              </span>
              <span className="scope-text">
                {t("socialListingAgent.scopeSessionLimitPrefix")}{" "}
                <span className="scope-emphasis">
                  {localizeNumbersInText(
                    t("socialListingAgent.scopeSessionLimitEmphasis")
                  )}
                </span>{" "}
                {t("socialListingAgent.scopeSessionLimitSuffix")}
              </span>
            </li>
          </ul>
          <p className="scope-note">
            {t("socialListingAgent.scopeNote")}
          </p>
        </Card>

        <div className="analysis-buttons" ref={analysisSectionRef}>
          {ANALYSIS_CARDS.map(({ titleKey, lineKeys, buttonKey, icon, route }) => (
            <Card key={titleKey} className="analysis-card" bordered={false}>
              <img src={icon} className="analysis-icon purple" alt="" />
              <h3>{t(`socialListingAgent.${titleKey}`)}</h3>
              <p>
                {t(`socialListingAgent.${lineKeys[0]}`)}
                <br />
                {t(`socialListingAgent.${lineKeys[1]}`)}
              </p>
              <button className="button button--primary" onClick={() => navigate(route)}>
                {t(`socialListingAgent.${buttonKey}`)}
              </button>
            </Card>
          ))}
        </div>
      </div>

      <div className="recent-reports">
        <Card className="recent-reports-card" bordered={false}>
          <div className="recent-reports-header">
            <h3 className="recent-reports-title">
              {t("socialListingAgent.recentReports")}
            </h3>
            <button
              type="button"
              className="button button--primary view-all-button"
              onClick={() => navigate(SOCIAL_LISTENING_ROUTES.history)}
            >
              {t("socialListingAgent.viewAll")}
            </button>
          </div>

          <div className="recent-reports-list">
            {recentHistory.length === 0 ? (
              <div className="recent-report-empty">
                {t("socialListingAgent.noReports")}
              </div>
            ) : (
              recentHistory.map(run => {
                const title = getRunTitle(run);
                const formattedDate = formatReportDate(run.completed_at ?? run.created_at, numberLocale);

                return (
                  <button
                    key={run.run_id}
                    type="button"
                    className="recent-report-item"
                    onClick={() => navigate(SOCIAL_LISTENING_ROUTES.report(run.run_id))}
                  >
                    <div className="recent-report-icon">
                      <FileTextOutlined />
                    </div>
                    <div className="recent-report-info">
                      <div className="recent-report-title">{title}</div>
                      {formattedDate && <div className="recent-report-date">{formattedDate}</div>}
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </Card>
      </div>
    </div>
  );
};

export default CompetitionOverview;
